import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { MediaPicker } from '@/components/feature/MediaPicker';
import { useSiteSettings } from '@/settings/useSiteSettings';
import { paymentGateways } from '@/services/paymentGateways';
import {
  DEFAULT_SITE_SETTINGS,
  mergeSiteSettings,
  type DashboardMenuItem,
  type GatewayCode,
  type HeaderStyle,
  type SiteLink,
  type SiteSettings,
  type SocialLink,
  type TaxDisplay,
} from '@/services/siteSettings';

const tabs = [
  { id: 'general', label: 'General', icon: 'ri-store-2-line' },
  { id: 'header', label: 'Header', icon: 'ri-layout-top-2-line' },
  { id: 'footer', label: 'Footer', icon: 'ri-layout-bottom-2-line' },
  { id: 'dashboard', label: 'Dashboard', icon: 'ri-dashboard-3-line' },
  { id: 'checkout', label: 'Checkout', icon: 'ri-shopping-cart-2-line' },
  { id: 'payments', label: 'Payments', icon: 'ri-bank-card-line' },
  {
    id: 'notifications',
    label: 'Notifications',
    icon: 'ri-notification-3-line',
  },
] as const;

type SettingsTab = (typeof tabs)[number]['id'];

const currencies = ['USD', 'EUR', 'GBP', 'BDT', 'AUD', 'CAD'];
const gatewayOptions: Array<{ code: GatewayCode; label: string }> = [
  { code: '2checkout', label: '2Checkout / Verifone' },
  { code: 'stripe', label: 'Stripe' },
  { code: 'paypal', label: 'PayPal' },
  { code: 'paddle', label: 'Paddle' },
  { code: 'sslcommerz', label: 'SSLCommerz' },
  { code: 'manual', label: 'Manual Payment' },
  { code: 'crypto', label: 'Cryptocurrency' },
];

function Card({
  title,
  description,
  children,
  className = '',
  headerAction,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
  headerAction?: ReactNode;
}) {
  return (
    <section
      className={`bg-white rounded-xl border border-surface-200 overflow-hidden ${className}`}>
      <div className="px-5 py-4 border-b border-surface-200 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-surface-900">{title}</h2>
          {description && (
            <p className="text-xs text-surface-500 mt-1">{description}</p>
          )}
        </div>
        {headerAction}
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </section>
  );
}

function Badge({
  children,
  variant = 'neutral',
}: {
  children: ReactNode;
  variant?: 'neutral' | 'success' | 'warning' | 'error' | 'info';
}) {
  const variants = {
    neutral: 'bg-surface-100 text-surface-600 border-surface-200',
    success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    warning: 'bg-amber-50 text-amber-700 border-amber-200',
    error: 'bg-red-50 text-red-700 border-red-200',
    info: 'bg-blue-50 text-blue-700 border-blue-200',
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${variants[variant]}`}>
      {children}
    </span>
  );
}

function StatusDot({ health }: { health: 'green' | 'yellow' | 'red' }) {
  const colors = {
    green: 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.4)]',
    yellow: 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.4)]',
    red: 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.4)]',
  };

  return <span className={`w-2 h-2 rounded-full ${colors[health]}`} />;
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-surface-700 mb-1.5">
        {label}
      </span>
      {children}
      {hint && (
        <span className="block text-xs text-surface-400 mt-1.5">{hint}</span>
      )}
    </label>
  );
}

function TextInput({
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 bg-white"
    />
  );
}

function Toggle({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="w-full flex items-center justify-between gap-4 rounded-lg border border-surface-200 px-3 py-3 text-left hover:border-surface-300 transition-colors">
      <span>
        <span className="block text-sm font-medium text-surface-800">
          {label}
        </span>
        {description && (
          <span className="block text-xs text-surface-500 mt-0.5">
            {description}
          </span>
        )}
      </span>
      <span
        className={`shrink-0 w-10 h-5 rounded-full p-0.5 transition-colors ${checked ? 'bg-brand-600' : 'bg-surface-300'}`}>
        <span
          className={`block w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${checked ? 'translate-x-5' : ''}`}
        />
      </span>
    </button>
  );
}

function moveItem<T>(items: T[], index: number, direction: -1 | 1) {
  const next = [...items];
  const target = index + direction;
  if (target < 0 || target >= next.length) return next;
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
}

function toTitleCase(value: string) {
  return value
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function LinkEditor({
  title,
  items,
  onChange,
  addLabel = 'Add Link',
}: {
  title: string;
  items: SiteLink[];
  onChange: (items: SiteLink[]) => void;
  addLabel?: string;
}) {
  const update = (index: number, patch: Partial<SiteLink>) => {
    onChange(
      items.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    );
  };

  return (
    <div className="rounded-xl border border-surface-200 overflow-hidden">
      <div className="px-4 py-3 bg-surface-50 border-b border-surface-200 flex items-center justify-between">
        <p className="text-sm font-semibold text-surface-800">{title}</p>
        <button
          type="button"
          onClick={() =>
            onChange([
              ...items,
              { id: crypto.randomUUID(), label: '', href: '/', enabled: true },
            ])
          }
          className="px-3 py-1.5 rounded-lg bg-white border border-surface-200 text-xs font-medium text-surface-700 hover:border-brand-300 hover:text-brand-600 transition-colors">
          <i className="ri-add-line mr-1" /> {addLabel}
        </button>
      </div>
      <div className="divide-y divide-surface-100">
        {items.map((item, index) => (
          <div
            key={item.id}
            className="p-4 grid grid-cols-1 lg:grid-cols-[1fr_1.3fr_auto] gap-3 items-center">
            <TextInput
              value={item.label}
              onChange={(value) => update(index, { label: value })}
              placeholder="Label"
            />
            <TextInput
              value={item.href}
              onChange={(value) => update(index, { href: value })}
              placeholder="/path or https://..."
            />
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => update(index, { enabled: !item.enabled })}
                className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.enabled ? 'bg-brand-50 text-brand-700' : 'bg-surface-100 text-surface-400'}`}
                title={item.enabled ? 'Enabled' : 'Disabled'}>
                <i
                  className={item.enabled ? 'ri-eye-line' : 'ri-eye-off-line'}
                />
              </button>
              <button
                type="button"
                onClick={() => onChange(moveItem(items, index, -1))}
                className="w-8 h-8 rounded-lg hover:bg-surface-100 text-surface-500"
                title="Move up">
                <i className="ri-arrow-up-s-line" />
              </button>
              <button
                type="button"
                onClick={() => onChange(moveItem(items, index, 1))}
                className="w-8 h-8 rounded-lg hover:bg-surface-100 text-surface-500"
                title="Move down">
                <i className="ri-arrow-down-s-line" />
              </button>
              <button
                type="button"
                onClick={() => onChange(items.filter((_, i) => i !== index))}
                className="w-8 h-8 rounded-lg hover:bg-red-50 text-red-500"
                title="Remove">
                <i className="ri-delete-bin-line" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MenuEditor({
  title,
  items,
  onChange,
  editableHref = false,
}: {
  title: string;
  items: DashboardMenuItem[];
  onChange: (items: DashboardMenuItem[]) => void;
  editableHref?: boolean;
}) {
  const update = (index: number, patch: Partial<DashboardMenuItem>) => {
    onChange(
      items.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    );
  };

  return (
    <div className="rounded-xl border border-surface-200 overflow-hidden">
      <div className="px-4 py-3 bg-surface-50 border-b border-surface-200">
        <p className="text-sm font-semibold text-surface-800">{title}</p>
      </div>
      <div className="divide-y divide-surface-100">
        {items.map((item, index) => (
          <div
            key={item.id}
            className="p-4 grid grid-cols-1 lg:grid-cols-[auto_1fr_1fr_auto] gap-3 items-center">
            <div className="w-9 h-9 rounded-lg bg-surface-100 text-surface-500 flex items-center justify-center">
              <i className={item.icon} />
            </div>
            <TextInput
              value={item.label}
              onChange={(value) => update(index, { label: value })}
              placeholder="Label"
            />
            {editableHref ? (
              <TextInput
                value={item.href}
                onChange={(value) => update(index, { href: value })}
                placeholder="/path"
              />
            ) : (
              <div className="px-3 py-2.5 rounded-lg bg-surface-50 border border-surface-200 text-xs text-surface-500 truncate">
                {item.href}
              </div>
            )}
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => update(index, { enabled: !item.enabled })}
                className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.enabled ? 'bg-brand-50 text-brand-700' : 'bg-surface-100 text-surface-400'}`}
                title={item.enabled ? 'Enabled' : 'Disabled'}>
                <i
                  className={item.enabled ? 'ri-eye-line' : 'ri-eye-off-line'}
                />
              </button>
              <button
                type="button"
                onClick={() => onChange(moveItem(items, index, -1))}
                className="w-8 h-8 rounded-lg hover:bg-surface-100 text-surface-500"
                title="Move up">
                <i className="ri-arrow-up-s-line" />
              </button>
              <button
                type="button"
                onClick={() => onChange(moveItem(items, index, 1))}
                className="w-8 h-8 rounded-lg hover:bg-surface-100 text-surface-500"
                title="Move down">
                <i className="ri-arrow-down-s-line" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SocialEditor({
  items,
  onChange,
}: {
  items: SocialLink[];
  onChange: (items: SocialLink[]) => void;
}) {
  const update = (index: number, patch: Partial<SocialLink>) => {
    onChange(
      items.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    );
  };

  return (
    <div className="rounded-xl border border-surface-200 overflow-hidden">
      <div className="px-4 py-3 bg-surface-50 border-b border-surface-200 flex items-center justify-between">
        <p className="text-sm font-semibold text-surface-800">Social Links</p>
        <button
          type="button"
          onClick={() =>
            onChange([
              ...items,
              {
                id: crypto.randomUUID(),
                label: '',
                href: '#',
                icon: 'ri-links-line',
                enabled: true,
              },
            ])
          }
          className="px-3 py-1.5 rounded-lg bg-white border border-surface-200 text-xs font-medium text-surface-700 hover:border-brand-300 hover:text-brand-600 transition-colors">
          <i className="ri-add-line mr-1" /> Add Social
        </button>
      </div>
      <div className="divide-y divide-surface-100">
        {items.map((item, index) => (
          <div
            key={item.id}
            className="p-4 grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-center">
            <TextInput
              value={item.label}
              onChange={(value) => update(index, { label: value })}
              placeholder="Label"
            />
            <TextInput
              value={item.href}
              onChange={(value) => update(index, { href: value })}
              placeholder="URL"
            />
            <TextInput
              value={item.icon}
              onChange={(value) => update(index, { icon: value })}
              placeholder="Remix icon class"
            />
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => update(index, { enabled: !item.enabled })}
                className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.enabled ? 'bg-brand-50 text-brand-700' : 'bg-surface-100 text-surface-400'}`}>
                <i
                  className={item.enabled ? 'ri-eye-line' : 'ri-eye-off-line'}
                />
              </button>
              <button
                type="button"
                onClick={() => onChange(items.filter((_, i) => i !== index))}
                className="w-8 h-8 rounded-lg hover:bg-red-50 text-red-500">
                <i className="ri-delete-bin-line" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ImageField({
  label,
  hint,
  value,
  onChange,
  pickerTitle,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  pickerTitle: string;
}) {
  const [showPicker, setShowPicker] = useState(false);

  return (
    <div>
      <span className="block text-sm font-medium text-surface-700 mb-1.5">
        {label}
      </span>
      <div className="flex items-center gap-3">
        {value ? (
          <img
            src={value}
            alt=""
            className="w-10 h-10 rounded-lg object-cover border border-surface-200 shrink-0"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className="w-10 h-10 rounded-lg bg-surface-100 border border-surface-200 flex items-center justify-center shrink-0">
            <i className="ri-image-line text-surface-400" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <input
            type="url"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://… or pick from media"
            className="w-full px-3 py-2 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 bg-white"
          />
        </div>
        <button
          type="button"
          onClick={() => setShowPicker(true)}
          className="shrink-0 px-3 py-2 rounded-lg border border-brand-300 bg-brand-50 hover:bg-brand-100 text-brand-600 text-xs font-semibold transition-colors flex items-center gap-1.5">
          <i className="ri-image-line" /> Pick
        </button>
      </div>
      {hint && (
        <span className="block text-xs text-surface-400 mt-1.5">{hint}</span>
      )}
      {showPicker && (
        <MediaPicker
          selected={value}
          title={pickerTitle}
          onSelect={(url) => {
            onChange(url);
          }}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}

export default function AdminSettings() {
  const {
    settings,
    loading,
    error: loadError,
    updateSettings,
  } = useSiteSettings();
  const [activeTab, setActiveTab] = useState<SettingsTab>('general');
  const [draft, setDraft] = useState<SiteSettings>(settings);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [copyState, setCopyState] = useState<Record<string, boolean>>({});
  const [testingGateway, setTestingGateway] = useState<GatewayCode | null>(
    null,
  );
  const [selectedGateway, setSelectedGateway] = useState<GatewayCode | null>(
    null,
  );
  const [configureGateway, setConfigureGateway] = useState<GatewayCode | null>(null);
  const [paymentNotice, setPaymentNotice] = useState('');

  useEffect(() => {
    setDraft(settings);
  }, [settings]);

  useEffect(() => {
    if (paymentNotice) {
      const timer = setTimeout(() => setPaymentNotice(''), 2400);
      return () => clearTimeout(timer);
    }
  }, [paymentNotice]);

  const activeTabLabel = useMemo(
    () => tabs.find((tab) => tab.id === activeTab)?.label ?? 'Settings',
    [activeTab],
  );

  const isValid = useMemo(() => {
    if (activeTab === 'payments') {
      const hasEnabledGateway = draft.payments.enabledGateways.length > 0;
      const isDefaultValid = draft.payments.enabledGateways.includes(
        draft.payments.defaultGateway,
      );
      const twoCheckoutRequiresCode =
        !draft.payments.enabledGateways.includes('2checkout') ||
        draft.payments.twoCheckoutMerchantCode.trim().length > 0;
      return hasEnabledGateway && isDefaultValid && twoCheckoutRequiresCode;
    }
    return true;
  }, [activeTab, draft.payments]);

  const enabledGatewayConfigs = useMemo(
    () =>
      paymentGateways.filter((gateway) =>
        draft.payments.enabledGateways.includes(gateway.code as GatewayCode),
      ),
    [draft.payments.enabledGateways],
  );

  const checkoutRoutes = useMemo(() => {
    const byCurrency = new Map<string, GatewayCode[]>();
    enabledGatewayConfigs.forEach((gateway) => {
      (gateway.supportedCurrencies ?? []).forEach((currency) => {
        const existing = byCurrency.get(currency) ?? [];
        byCurrency.set(currency, [...existing, gateway.code as GatewayCode]);
      });
    });

    return Array.from(byCurrency.entries())
      .map(([currency, gateways]) => ({
        currency,
        primary: gateways[0],
        fallback: gateways[1] ?? null,
      }))
      .slice(0, 6);
  }, [enabledGatewayConfigs]);

  const selectedGatewayConfig = useMemo(
    () =>
      selectedGateway
        ? paymentGateways.find((gateway) => gateway.code === selectedGateway)
        : null,
    [selectedGateway],
  );

  const copyWebhookUrl = async (gatewayCode: GatewayCode, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopyState((current) => ({ ...current, [gatewayCode]: true }));
      setTimeout(() => {
        setCopyState((current) => ({ ...current, [gatewayCode]: false }));
      }, 1500);
    } catch {
      setPaymentNotice(
        'Clipboard access is unavailable in this browser context.',
      );
    }
  };

  const runGatewayTest = async (gatewayCode: GatewayCode) => {
    setTestingGateway(gatewayCode);
    await new Promise((resolve) => setTimeout(resolve, 700));
    setTestingGateway(null);
    setPaymentNotice(
      `${gatewayOptions.find((option) => option.code === gatewayCode)?.label ?? gatewayCode} test ping completed.`,
    );
  };

  const setSection = <K extends keyof SiteSettings>(
    section: K,
    patch: Partial<SiteSettings[K]> | ((current: SiteSettings[K]) => Partial<SiteSettings[K]>),
  ) => {
    setDraft((current) => {
      const resolved = typeof patch === 'function' ? patch(current[section]) : patch;
      return {
        ...current,
        [section]: {
          ...current[section],
          ...resolved,
        },
      };
    });
  };

  const patchNowPayments = (patch: Partial<typeof draft.payments.nowpayments>) =>
    setSection('payments', (p) => ({ nowpayments: { ...p.nowpayments, ...patch } }));

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      const next = await updateSettings(draft);
      setDraft(next);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => {
    setDraft(mergeSiteSettings(DEFAULT_SITE_SETTINGS));
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-surface-900">
            Settings
          </h1>
          <p className="text-sm text-surface-500 mt-1">
            Control store identity, navigation, checkout, and dashboard
            presentation.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={resetDefaults}
            className="px-4 py-2 rounded-lg border border-surface-300 bg-white text-surface-700 hover:border-surface-400 text-sm font-medium transition-colors">
            Reset Draft
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !isValid}
            className="px-5 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 disabled:bg-surface-300 text-white text-sm font-semibold transition-colors flex items-center gap-2">
            {saving && <i className="ri-loader-4-line animate-spin" />}
            {saved ? 'Saved' : saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      {(error || loadError) && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          {error || loadError}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[230px_minmax(0,1fr)] gap-5">
        <aside className="bg-white rounded-xl border border-surface-200 p-2 h-fit xl:sticky xl:top-20">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-brand-50 text-brand-700'
                  : 'text-surface-600 hover:bg-surface-50 hover:text-surface-900'
              }`}>
              <i className={`${tab.icon} text-base`} />
              {tab.label}
            </button>
          ))}
        </aside>

        <div className="space-y-5">
          <div className="bg-surface-900 rounded-xl p-5 text-white flex items-center justify-between">
            <div>
              <p className="text-xs text-surface-400 uppercase font-semibold tracking-wide">
                {loading ? 'Loading' : 'Editing'}
              </p>
              <h2 className="text-lg font-bold mt-1">{activeTabLabel}</h2>
            </div>
            <div className="hidden sm:flex items-center gap-3">
              {draft.general.logoUrl && (
                <img
                  src={draft.general.logoUrl}
                  alt=""
                  className="w-9 h-9 rounded object-contain bg-white/10"
                />
              )}
              <div className="text-right">
                <p className="text-sm font-semibold">
                  {draft.general.storeName}
                </p>
                <p className="text-xs text-surface-400">
                  {draft.general.supportEmail}
                </p>
              </div>
            </div>
          </div>

          {activeTab === 'general' && (
            <>
              <Card
                title="Store Identity"
                description="Used across browser metadata, public layout, dashboard chrome, and customer emails.">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Store Name">
                    <TextInput
                      value={draft.general.storeName}
                      onChange={(storeName) =>
                        setSection('general', { storeName })
                      }
                    />
                  </Field>
                  <Field label="Short Brand Name">
                    <TextInput
                      value={draft.general.shortName}
                      onChange={(shortName) =>
                        setSection('general', { shortName })
                      }
                    />
                  </Field>
                  <ImageField
                    label="Store Logo"
                    value={draft.general.logoUrl}
                    onChange={(logoUrl) => setSection('general', { logoUrl })}
                    pickerTitle="Choose Store Logo"
                  />
                  <ImageField
                    label="Dashboard Logo"
                    value={draft.general.dashboardLogoUrl}
                    onChange={(dashboardLogoUrl) =>
                      setSection('general', { dashboardLogoUrl })
                    }
                    pickerTitle="Choose Dashboard Logo"
                  />
                  <ImageField
                    label="Favicon"
                    value={draft.general.faviconUrl}
                    onChange={(faviconUrl) =>
                      setSection('general', { faviconUrl })
                    }
                    pickerTitle="Choose Favicon"
                  />
                  <Field label="Default Currency">
                    <select
                      value={draft.general.defaultCurrency}
                      onChange={(e) =>
                        setSection('general', {
                          defaultCurrency: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 bg-white">
                      {currencies.map((currency) => (
                        <option key={currency} value={currency}>
                          {currency}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Support Email">
                    <TextInput
                      type="email"
                      value={draft.general.supportEmail}
                      onChange={(supportEmail) =>
                        setSection('general', { supportEmail })
                      }
                    />
                  </Field>
                  <Field label="Support URL">
                    <TextInput
                      value={draft.general.supportUrl}
                      onChange={(supportUrl) =>
                        setSection('general', { supportUrl })
                      }
                    />
                  </Field>
                  <Field label="Support Response Time">
                    <TextInput
                      value={draft.general.supportResponseTime}
                      onChange={(supportResponseTime) =>
                        setSection('general', { supportResponseTime })
                      }
                      placeholder="e.g. < 4 hours avg"
                    />
                  </Field>
                  <Field label="404 Redirect URL" hint="Where to redirect when a page is not found. Default: /products">
                    <TextInput
                      value={draft.general.notFoundUrl}
                      onChange={(v) => setSection('general', { notFoundUrl: v })}
                      placeholder="/products"
                    />
                  </Field>
                </div>
              </Card>
            </>
          )}

          {activeTab === 'header' && (
            <>
              <Card
                title="Header Brand"
                description="Controls the public site header shown to visitors and signed-in users.">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Header Brand Text">
                    <TextInput
                      value={draft.header.brandText}
                      onChange={(brandText) =>
                        setSection('header', { brandText })
                      }
                    />
                  </Field>
                  <Field label="Header Logo URL">
                    <TextInput
                      value={draft.header.logoUrl}
                      onChange={(logoUrl) => setSection('header', { logoUrl })}
                    />
                  </Field>
                  <Field label="Header Style">
                    <select
                      value={draft.header.style}
                      onChange={(e) =>
                        setSection('header', {
                          style: e.target.value as HeaderStyle,
                        })
                      }
                      className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 bg-white">
                      <option value="auto">Auto</option>
                      <option value="light">Light</option>
                      <option value="dark">Dark</option>
                    </select>
                  </Field>
                  <Toggle
                    checked={draft.header.sticky}
                    onChange={(sticky) => setSection('header', { sticky })}
                    label="Sticky Header"
                    description="Keep the header fixed at the top while scrolling."
                  />
                </div>
              </Card>

              <Card title="Guest Actions">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Primary Button Label">
                    <TextInput
                      value={draft.header.loggedOutPrimaryLabel}
                      onChange={(loggedOutPrimaryLabel) =>
                        setSection('header', { loggedOutPrimaryLabel })
                      }
                    />
                  </Field>
                  <Field label="Primary Button Link">
                    <TextInput
                      value={draft.header.loggedOutPrimaryHref}
                      onChange={(loggedOutPrimaryHref) =>
                        setSection('header', { loggedOutPrimaryHref })
                      }
                    />
                  </Field>
                  <Field label="Secondary Button Label">
                    <TextInput
                      value={draft.header.loggedOutSecondaryLabel}
                      onChange={(loggedOutSecondaryLabel) =>
                        setSection('header', { loggedOutSecondaryLabel })
                      }
                    />
                  </Field>
                  <Field label="Secondary Button Link">
                    <TextInput
                      value={draft.header.loggedOutSecondaryHref}
                      onChange={(loggedOutSecondaryHref) =>
                        setSection('header', { loggedOutSecondaryHref })
                      }
                    />
                  </Field>
                </div>
              </Card>

              <LinkEditor
                title="Public Navigation"
                items={draft.header.menuItems}
                onChange={(menuItems) => setSection('header', { menuItems })}
              />
              <MenuEditor
                title="Signed-in Profile Menu"
                items={draft.header.profileMenuItems}
                onChange={(profileMenuItems) =>
                  setSection('header', { profileMenuItems })
                }
                editableHref
              />
            </>
          )}

          {activeTab === 'footer' && (
            <>
              <Card title="Footer Brand">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Footer Brand Name">
                    <TextInput
                      value={draft.footer.brandName}
                      onChange={(brandName) =>
                        setSection('footer', { brandName })
                      }
                    />
                  </Field>
                  <Field label="Footer Logo URL">
                    <TextInput
                      value={draft.footer.logoUrl}
                      onChange={(logoUrl) => setSection('footer', { logoUrl })}
                    />
                  </Field>
                  <Field label="Contact Link">
                    <TextInput
                      value={draft.footer.contactHref}
                      onChange={(contactHref) =>
                        setSection('footer', { contactHref })
                      }
                    />
                  </Field>
                  <Field label="Copyright Text">
                    <TextInput
                      value={draft.footer.copyrightText}
                      onChange={(copyrightText) =>
                        setSection('footer', { copyrightText })
                      }
                    />
                  </Field>
                </div>
                <Field label="Footer Description">
                  <textarea
                    value={draft.footer.description}
                    onChange={(e) =>
                      setSection('footer', { description: e.target.value })
                    }
                    rows={3}
                    className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 resize-none"
                  />
                </Field>
              </Card>

              <LinkEditor
                title="Product Links"
                items={draft.footer.productLinks}
                onChange={(productLinks) =>
                  setSection('footer', { productLinks })
                }
              />
              <LinkEditor
                title="Support Links"
                items={draft.footer.supportLinks}
                onChange={(supportLinks) =>
                  setSection('footer', { supportLinks })
                }
              />
              <LinkEditor
                title="Company Links"
                items={draft.footer.companyLinks}
                onChange={(companyLinks) =>
                  setSection('footer', { companyLinks })
                }
              />
              <SocialEditor
                items={draft.footer.socialLinks}
                onChange={(socialLinks) =>
                  setSection('footer', { socialLinks })
                }
              />
            </>
          )}

          {activeTab === 'dashboard' && (
            <>
              <Card
                title="Dashboard Chrome"
                description="Controls shared labels and navigation used by admin and customer dashboards.">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Admin Sidebar Title">
                    <TextInput
                      value={draft.dashboard.adminSidebarTitle}
                      onChange={(adminSidebarTitle) =>
                        setSection('dashboard', { adminSidebarTitle })
                      }
                    />
                  </Field>
                  <Field label="Customer Sidebar Title">
                    <TextInput
                      value={draft.dashboard.customerSidebarTitle}
                      onChange={(customerSidebarTitle) =>
                        setSection('dashboard', { customerSidebarTitle })
                      }
                    />
                  </Field>
                  <Field label="Admin Top Bar Title">
                    <TextInput
                      value={draft.dashboard.adminTopbarTitle}
                      onChange={(adminTopbarTitle) =>
                        setSection('dashboard', { adminTopbarTitle })
                      }
                    />
                  </Field>
                  <Field label="Customer Top Bar Title">
                    <TextInput
                      value={draft.dashboard.customerTopbarTitle}
                      onChange={(customerTopbarTitle) =>
                        setSection('dashboard', { customerTopbarTitle })
                      }
                    />
                  </Field>
                  <Toggle
                    checked={draft.dashboard.showVisitShop}
                    onChange={(showVisitShop) =>
                      setSection('dashboard', { showVisitShop })
                    }
                    label="Show Visit Shop Shortcut"
                    description="Display the external shop shortcut above dashboard navigation."
                  />
                </div>
              </Card>

              <MenuEditor
                title="Admin Sidebar Menu"
                items={draft.dashboard.adminMenu}
                onChange={(adminMenu) => setSection('dashboard', { adminMenu })}
              />
              <MenuEditor
                title="Customer Sidebar Menu"
                items={draft.dashboard.customerMenu}
                onChange={(customerMenu) =>
                  setSection('dashboard', { customerMenu })
                }
              />
              <MenuEditor
                title="Customer Quick Actions"
                items={draft.dashboard.quickActions}
                onChange={(quickActions) =>
                  setSection('dashboard', { quickActions })
                }
                editableHref
              />
            </>
          )}

          {activeTab === 'checkout' && (
            <div className="space-y-6">
              <Card title="Checkout Behavior" description="Control how the checkout page behaves for customers.">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Toggle checked={draft.checkout.requireAccount} onChange={(v) => setSection('checkout', { requireAccount: v })} label="Require Account" description="Customers must sign in before purchasing" />
                  <Toggle checked={draft.checkout.guestCheckout} onChange={(v) => setSection('checkout', { guestCheckout: v })} label="Guest Checkout" description="Allow purchases without an account" />
                  <Toggle checked={draft.checkout.showPricingOnCards} onChange={(v) => setSection('checkout', { showPricingOnCards: v })} label="Show Pricing on Cards" description="Display prices on product listing cards" />
                  <Field label="Tax Display">
                    <select value={draft.checkout.taxDisplay} onChange={(e) => setSection('checkout', { taxDisplay: e.target.value as TaxDisplay })}
                      className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 bg-white">
                      <option value="exclusive">Show prices excluding tax</option>
                      <option value="inclusive">Show prices including tax</option>
                      <option value="hidden">Hide tax messaging</option>
                    </select>
                  </Field>
                  <Field label="Success Page" hint="Where customers go after a successful order">
                    <TextInput value={draft.checkout.successUrl} onChange={(v) => setSection('checkout', { successUrl: v })} />
                  </Field>
                </div>
              </Card>
            </div>
          )}

          {activeTab === 'payments' && (
            <div className="space-y-6">
              {/* Gateways */}
              <Card title="Payment Gateways" description="Enable and configure payment providers for your store.">
                <div className="space-y-4">
                  {paymentGateways.map((gw) => {
                    const enabled = draft.payments.enabledGateways.includes(gw.code);
                    return (
                      <div key={gw.code} className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${enabled ? 'border-surface-200 bg-white' : 'border-surface-100 bg-surface-50/50'}`}>
                        <div className="w-10 h-10 rounded-xl bg-surface-50 border border-surface-100 flex items-center justify-center p-2 shrink-0 overflow-hidden">
                          {gw.logoUrl ? <img src={gw.logoUrl} alt={gw.name} className="max-w-full max-h-full object-contain" /> : <i className="ri-bank-card-line text-surface-400 text-lg" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-surface-900">{gw.name}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-surface-100 text-surface-500 font-medium">{gw.code}</span>
                          </div>
                          <p className="text-xs text-surface-400 mt-0.5">{gw.onboardingHint}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button type="button" onClick={() => setConfigureGateway(gw.code as GatewayCode)}
                            className="px-3 py-1.5 rounded-lg border border-surface-200 hover:border-brand-400 hover:text-brand-600 text-surface-500 text-xs font-medium transition-colors">
                            Configure
                          </button>
                          <Toggle checked={enabled} onChange={(checked) => {
                            const list = checked ? [...draft.payments.enabledGateways, gw.code] : draft.payments.enabledGateways.filter((c) => c !== gw.code);
                            setSection('payments', { enabledGateways: list, defaultGateway: list.includes(draft.payments.defaultGateway) ? draft.payments.defaultGateway : (list[0] || '2checkout') });
                          }} label="" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* Default Gateway */}
              <Card title="Default Gateway" description="This gateway is preselected when customers reach checkout.">
                <Field label="Primary Gateway">
                  <select value={draft.payments.defaultGateway} onChange={(e) => setSection('payments', { defaultGateway: e.target.value as GatewayCode })}
                    className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 bg-white max-w-xs">
                    {paymentGateways.filter((g) => draft.payments.enabledGateways.includes(g.code)).map((g) => (
                      <option key={g.code} value={g.code}>{g.name}</option>
                    ))}
                  </select>
                </Field>
              </Card>
            </div>
          )}

          {/* Gateway Configuration Modal */}
          {configureGateway && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setConfigureGateway(null)} />
              <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden">
                <div className="px-6 py-4 border-b border-surface-200 flex items-center justify-between">
                  <h3 className="font-semibold text-surface-900 text-sm flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-brand-50 flex items-center justify-center">
                      <i className="ri-settings-3-line text-brand-600 text-xs" />
                    </div>
                    Configure {gatewayOptions.find((g) => g.code === configureGateway)?.label}
                  </h3>
                  <button onClick={() => setConfigureGateway(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-100 text-surface-500">
                    <i className="ri-close-line text-lg" />
                  </button>
                </div>

                <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
                  {configureGateway === 'stripe' && (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-surface-700 mb-2">Environment</label>
                        <div className="flex p-1 bg-surface-100 rounded-xl w-fit">
                          <button type="button" onClick={() => setSection('payments', { mode: 'test' })}
                            className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${draft.payments.mode === 'test' ? 'bg-white text-surface-900 shadow-sm' : 'text-surface-500 hover:text-surface-700'}`}>Sandbox</button>
                          <button type="button" onClick={() => setSection('payments', { mode: 'live' })}
                            className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${draft.payments.mode === 'live' ? 'bg-white text-surface-900 shadow-sm' : 'text-surface-500 hover:text-surface-700'}`}>Live</button>
                        </div>
                      </div>
                      <div className="border-t border-surface-100" />
                      <Field label="Publishable Key" hint="Starts with pk_live or pk_test">
                        <TextInput value={draft.payments.stripe.publishableKey} onChange={(v) => setSection('payments', { stripe: { ...draft.payments.stripe, publishableKey: v } })} placeholder="pk_live_..." />
                      </Field>
                      <Field label="Secret Key" hint="Starts with sk_live or sk_test. Server-side only.">
                        <TextInput value={draft.payments.stripe.secretKey} onChange={(v) => setSection('payments', { stripe: { ...draft.payments.stripe, secretKey: v } })} placeholder="sk_live_..." type="password" />
                      </Field>
                      <Field label="Webhook Secret" hint="Starts with whsec_. Verifies Stripe events.">
                        <TextInput value={draft.payments.stripe.webhookSecret} onChange={(v) => setSection('payments', { stripe: { ...draft.payments.stripe, webhookSecret: v } })} placeholder="whsec_..." type="password" />
                      </Field>
                    </>
                  )}
                  {configureGateway === 'paypal' && (
                    <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800">
                      <p className="font-semibold mb-1">PayPal Configuration</p>
                      <p>PayPal client ID and secret can be configured here once supported.</p>
                    </div>
                  )}
                  {configureGateway === '2checkout' && (
                    <Field label="Merchant Code" hint="Your 2Checkout / Verifone merchant code.">
                      <TextInput value={draft.payments.twoCheckoutMerchantCode} onChange={(v) => setSection('payments', { twoCheckoutMerchantCode: v })} placeholder="MERCHANT_CODE" />
                    </Field>
                  )}
                  {configureGateway === 'manual' && (
                    <Field label="Contact Link" hint="Customers see this link to contact you for payment instructions.">
                      <TextInput value={draft.payments.manualContactLink} onChange={(v) => setSection('payments', { manualContactLink: v })} placeholder="https://store.motionsdio.com/contact or mailto:sales@..." />
                    </Field>
                  )}
                  {configureGateway === 'crypto' && (
                    <>
                      {/* NOWPayments Toggle */}
                      <div className="flex items-center justify-between p-4 rounded-xl bg-surface-50 border border-surface-200">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center">
                            <i className="ri-funds-line text-brand-600 text-sm" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-surface-900">NOWPayments</p>
                            <p className="text-xs text-surface-400">Automated crypto payment gateway</p>
                          </div>
                        </div>
                        <Toggle checked={draft.payments.nowpayments.enabled} onChange={(v) => patchNowPayments({ enabled: v })} label="" />
                      </div>

                      {draft.payments.nowpayments.enabled && (
                        <div className="space-y-4 pl-4 border-l-2 border-brand-200">
                          <div>
                            <label className="block text-xs font-medium text-surface-700 mb-2">Environment</label>
                            <div className="flex p-1 bg-surface-100 rounded-xl w-fit">
                              <button type="button" onClick={() => setSection('payments', { mode: 'test' })}
                                className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${draft.payments.mode === 'test' ? 'bg-white text-surface-900 shadow-sm' : 'text-surface-500 hover:text-surface-700'}`}>Sandbox</button>
                              <button type="button" onClick={() => setSection('payments', { mode: 'live' })}
                                className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${draft.payments.mode === 'live' ? 'bg-white text-surface-900 shadow-sm' : 'text-surface-500 hover:text-surface-700'}`}>Live</button>
                            </div>
                          </div>
                          <div className="border-t border-surface-100" />
                          <div className="flex items-center gap-2 text-xs text-surface-500">
                            <i className="ri-information-line" />
                            <span>Get your API keys from <a href="https://nowpayments.io" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">nowpayments.io</a></span>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <Field label="API Key" hint="From NOWPayments dashboard">
                              <TextInput value={draft.payments.nowpayments.apiKey} onChange={(v) => patchNowPayments({ apiKey: v })} placeholder="XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX" type="password" />
                            </Field>
                            <Field label="Public Key" hint="From NOWPayments dashboard">
                              <TextInput value={draft.payments.nowpayments.publicKey} onChange={(v) => patchNowPayments({ publicKey: v })} placeholder="XXXXX-XXXXX-XXXXX-XXXXX-XXXXX" type="password" />
                            </Field>
                            <Field label="IPN Secret Key" hint="Used to verify webhook signatures">
                              <TextInput value={draft.payments.nowpayments.ipnSecret} onChange={(v) => patchNowPayments({ ipnSecret: v })} placeholder="your-ipn-secret" type="password" />
                            </Field>
                            <Field label="Payout Wallet" hint="Where crypto payouts are sent">
                              <TextInput value={draft.payments.nowpayments.payoutWallet} onChange={(v) => patchNowPayments({ payoutWallet: v })} placeholder="0x... or bc1q..." />
                            </Field>
                          </div>

                          {/* IPN URL */}
                          <div className="p-3 rounded-xl bg-blue-50 border border-blue-200">
                            <p className="text-xs font-semibold text-blue-700 mb-1">IPN / Webhook URL</p>
                            <p className="text-xs text-blue-600 font-mono break-all bg-white px-3 py-2 rounded-lg border border-blue-100">{draft.payments.nowpayments.ipnUrl}</p>
                            <p className="text-xs text-blue-500 mt-1">Set this URL in your NOWPayments dashboard → IPN Settings</p>
                          </div>

                          {/* Currency selections */}
                          <div className="grid grid-cols-2 gap-4">
                            <Field label="Accepted Currencies" hint="Comma-separated coin codes">
                              <TextInput value={draft.payments.nowpayments.acceptedCurrencies} onChange={(v) => patchNowPayments({ acceptedCurrencies: v })} placeholder="BTC,ETH,USDT,BNB,USDC" />
                            </Field>
                            <Field label="Default Pay Currency" hint="e.g. USDTBSC, BTC, ETH, BNB">
                              <TextInput value={draft.payments.nowpayments.defaultPayCurrency} onChange={(v) => patchNowPayments({ defaultPayCurrency: v })} placeholder="USDTBSC" />
                            </Field>
                            <Field label="Payout Currency" hint="Currency for auto-withdrawals">
                              <TextInput value={draft.payments.nowpayments.payoutCurrency} onChange={(v) => patchNowPayments({ payoutCurrency: v })} placeholder="USDTBSC" />
                            </Field>
                          </div>

                          {/* Order status mapping */}
                          <div className="grid grid-cols-2 gap-4">
                            <Field label="Status: Successful Payment" hint="Order status after payment confirmed">
                              <select value={draft.payments.nowpayments.statusPaid} onChange={(e) => patchNowPayments({ statusPaid: e.target.value })}
                                className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 bg-white">
                                <option value="paid">Paid</option>
                                <option value="pending">Pending</option>
                              </select>
                            </Field>
                            <Field label="Status: Failed/Expired" hint="Order status after payment fails">
                              <select value={draft.payments.nowpayments.statusFailed} onChange={(e) => patchNowPayments({ statusFailed: e.target.value })}
                                className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 bg-white">
                                <option value="failed">Failed</option>
                                <option value="pending">Pending</option>
                              </select>
                            </Field>
                          </div>
                        </div>
                      )}

                      {/* Manual wallet fallback */}
                      <div className="border-t border-surface-100 pt-4">
                        <details className="group">
                          <summary className="text-xs font-medium text-surface-500 cursor-pointer hover:text-surface-700 select-none flex items-center gap-1">
                            <i className="ri-arrow-right-s-line group-open:rotate-90 transition-transform text-xs" />
                            Manual wallet fallback
                          </summary>
                          <div className="mt-3 space-y-3">
                            <Field label="Wallet Address (optional)" hint="Fallback if NOWPayments is unavailable">
                              <TextInput value="" onChange={() => {}} placeholder="bc1q... or 0x... or TBit..." />
                            </Field>
                            <p className="text-xs text-surface-400">Admin manually verifies and confirms the order.</p>
                          </div>
                        </details>
                      </div>
                    </>
                  )}
                  {(configureGateway === 'paddle' || configureGateway === 'sslcommerz') && (
                    <div className="p-4 rounded-xl bg-surface-50 border border-surface-200 text-sm text-surface-500">
                      Configuration for {gatewayOptions.find((g) => g.code === configureGateway)?.label} will be available in a future update.
                    </div>
                  )}
                </div>

                <div className="px-6 py-4 border-t border-surface-200 flex items-center justify-end bg-surface-50/50">
                  <button onClick={() => setConfigureGateway(null)} className="px-5 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors">Done</button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <Card
              title="Notification Rules"
              description="Controls which notification types appear in dashboard notification feeds.">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Toggle
                  checked={draft.notifications.adminEnabled}
                  onChange={(adminEnabled) =>
                    setSection('notifications', { adminEnabled })
                  }
                  label="Enable Admin Notifications"
                />
                <Toggle
                  checked={draft.notifications.customerEnabled}
                  onChange={(customerEnabled) =>
                    setSection('notifications', { customerEnabled })
                  }
                  label="Enable Customer Notifications"
                />
                <Toggle
                  checked={draft.notifications.newOrder}
                  onChange={(newOrder) =>
                    setSection('notifications', { newOrder })
                  }
                  label="New Order Notifications"
                />
                <Toggle
                  checked={draft.notifications.licenseActivation}
                  onChange={(licenseActivation) =>
                    setSection('notifications', { licenseActivation })
                  }
                  label="License Activation Notifications"
                />
                <Toggle
                  checked={draft.notifications.webhookFailure}
                  onChange={(webhookFailure) =>
                    setSection('notifications', { webhookFailure })
                  }
                  label="Webhook Failure Notifications"
                />
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
